'use client'

import { useEffect, useRef, useState } from 'react'

import { useCookieConsentContext } from '@graftcode/design-system'
import { GoogleTagManager } from '@next/third-parties/google'

import { getGtmConfig } from '../../utils/gtm'

type GtmConsentMode = 'default' | 'update'

type CookieConsent = {
  analytics?: boolean
  marketing?: boolean
  performance?: boolean
}

type GtmConsentPayload = {
  analytics_storage: 'granted' | 'denied'
  ad_storage: 'granted' | 'denied'
  ad_user_data: 'granted' | 'denied'
  ad_personalization: 'granted' | 'denied'
  functionality_storage: 'granted' | 'denied'
}

const DEFAULT_DENIED_CONSENT: CookieConsent = {
  analytics: false,
  marketing: false,
  performance: false,
}

function mapCookieConsentToGtm(consent: CookieConsent): GtmConsentPayload {
  const analyticsStorage = consent.analytics ? 'granted' : 'denied'
  const adStorage = consent.marketing ? 'granted' : 'denied'
  const functionalityStorage = consent.performance ? 'granted' : 'denied'

  return {
    analytics_storage: analyticsStorage,
    ad_storage: adStorage,
    ad_user_data: adStorage,
    ad_personalization: adStorage,
    functionality_storage: functionalityStorage,
  }
}

function pushGtmConsent(mode: GtmConsentMode, consent: CookieConsent) {
  if (typeof window === 'undefined') return

  const gtmWindow = window as Window & { dataLayer?: unknown[] }
  gtmWindow.dataLayer = gtmWindow.dataLayer || []
  gtmWindow.dataLayer.push(['consent', mode, mapCookieConsentToGtm(consent)])
}

export function GtmConsentSync() {
  const { consent, isInitialized } = useCookieConsentContext()
  const hasInitializedGtmConsentRef = useRef(false)
  const [hasMountedGtm, setHasMountedGtm] = useState(false)

  const { gtmId, auth: gtmAuth, preview: gtmPreview } = getGtmConfig()

  useEffect(() => {
    if (!isInitialized || hasInitializedGtmConsentRef.current) return

    pushGtmConsent('default', DEFAULT_DENIED_CONSENT)
    hasInitializedGtmConsentRef.current = true
  }, [isInitialized])

  useEffect(() => {
    if (!isInitialized || !consent) return

    pushGtmConsent('update', consent)
  }, [consent, isInitialized])

  useEffect(() => {
    if (!gtmId || !isInitialized || !consent?.analytics) return

    setHasMountedGtm(true)
  }, [consent?.analytics, gtmId, isInitialized])

  if (!hasMountedGtm || !gtmId) {
    return null
  }

  return <GoogleTagManager gtmId={gtmId} auth={gtmAuth} preview={gtmPreview} />
}
